import {
    CheckboxChangeEvent,
    ColorPickerChangeEvent,
    NumericTextBoxChangeEvent,
    RadioButtonProps,
    RadioGroupChangeEvent,
    SwitchChangeEvent,
    TextAreaChangeEvent,
    TextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import React from 'react';
import { DropdownList, DropdownListWithValueFieldChangeEvent } from '../../../components/inputs/DropdownListWithValueField';
import { FilterGroupType } from '../../../../types/filter-group';
import useFilterGroup from '../../../../hooks/use-filter-group';

function createHook<T, E>(handler: (e: E) => T) {
    return (initialValue: T) => {
        const [value, setValue] = React.useState<T>(initialValue);
        const onChange = React.useCallback((e: E) => {
            setValue(handler(e));
        }, []);

        return {
            value,
            onChange
        };
    };
}

function createHookChecked<T, E>(handler: (e: E) => T) {
    return (initialValue: T) => {
        const [value, setValue] = React.useState<T>(initialValue);
        const onChange = React.useCallback((e: E) => {
            setValue(handler(e));
        }, []);

        return {
            checked: value,
            onChange
        };
    };
}

export const useFormColorValue = createHook<string, ColorPickerChangeEvent>((e) => e.value as string);
export const useFormStringValue = createHook<string, TextBoxChangeEvent>((e) => e.value as string);
export const useFormTextAreaValue = createHook<string, TextAreaChangeEvent>((e) => e.value as string);
export const useFormNumericValue = createHook<number | null, NumericTextBoxChangeEvent>((e) => e.value);
export const useFormSwitchValue = createHookChecked<boolean, SwitchChangeEvent>((e) => e.value);
export const useFormCheckboxValue = createHook<boolean, CheckboxChangeEvent>((e) => e.value);

export type RadioButtonList<T> = Array<Omit<RadioButtonProps, 'value'> & { value: T }>;
export function useFormRadioValue<T>(data: RadioButtonList<T>, initialValue: T) {
    const [value, setValue] = React.useState<T>(initialValue);
    const onChange = React.useCallback((e: RadioGroupChangeEvent) => {
        setValue(e.value);
    }, []);

    return {
        value,
        data,
        onChange
    };
}

export function useFormDropdownValue<T>(data: DropdownList<T>, initialValue: T) {
    const [value, setValue] = React.useState<T>(initialValue);
    const onChange = React.useCallback((e: DropdownListWithValueFieldChangeEvent<T>) => {
        setValue(e.value);
    }, []);

    return {
        value,
        data,
        onChange
    };
}

export function useFilterGroupDropdownValue(filterGroupType: FilterGroupType, initialIdValue?: string) {
    const filterGroups = useFilterGroup(filterGroupType);
    const data = React.useMemo(() => {
        return filterGroups.map((fg) => ({ value: fg.id, text: fg.name }));
    }, [filterGroups]);

    return useFormDropdownValue(data, initialIdValue);
}

export function useNullableFilterGroupDropdownValue(filterGroupType: FilterGroupType, initialIdValue?: string) {
    const filterGroups = useFilterGroup(filterGroupType);
    const data = React.useMemo(() => {
        return [{ value: '', text: 'None' }].concat(filterGroups.map((fg) => ({ value: fg.id, text: fg.name })));
    }, [filterGroups]);

    return useFormDropdownValue(data, initialIdValue ?? '');
}

export function useFormUpperCaseStringValue(initial: string)  {
    const field = useFormStringValue(initial);
    return {
        ...field,
        onChange: (e:TextBoxChangeEvent ) => {
            const upperCaseValue = (e.value as string).toUpperCase();
            field.onChange({ ...e, value: upperCaseValue } as TextBoxChangeEvent);
        }
    }
}

export function useFormUpperCaseTextAreaValue(initial: string)  {
    const field = useFormTextAreaValue(initial);
    return {
        ...field,
        onChange: (e:TextAreaChangeEvent ) => {
            const upperCaseValue = (e.value as string).toUpperCase();
            field.onChange({ ...e, value: upperCaseValue } as TextAreaChangeEvent);
        }
    }
}
